import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur, minimoMsg } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SLIDER_MIN, SLIDER_MAX, sliderMinFor } from "@/lib/constants";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Wallet, Save, Loader2, Lock, HeartPulse } from "lucide-react";

const UW_BOOLS = [
  ["contratto_annullato_sinistro", "Contratto precedente annullato a seguito di sinistro"],
  ["liti_in_corso", "Liti / controversie in corso"],
  ["sostituzione_altra_compagnia", "Sostituzione di polizza in essere con altra compagnia"],
];

// Mappa "categoria assicurati" del link pubblico → codice categoria del motore
const INTAKE_CAT_MAP = {
  "Personale dipendente (subordinato/parasubordinato, tirocinanti)": "dipendenti",
  "Collaboratori / personale occasionale": "collaboratori",
  "Clero (parroci, sacerdoti, suore, laici)": "clero",
  "Soci e amministratori": "soci_amministratori",
  "Utenti / assistiti / ospiti / partecipanti": "utenti",
  "Sport (atleti / tesserati)": "sport",
};

export default function InfQuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const editId = params.get("edit");
  const { isAdmin, user } = useAuth();
  const sliderMin = sliderMinFor(user?.role);
  const intakeId = params.get("intake");
  const [editLoaded, setEditLoaded] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const editRef = useRef(null);
  const intakeRef = useRef(null);

  const [categoria, setCategoria] = useState("dipendenti");
  const [modalita, setModalita] = useState("");
  const [tipoRischio, setTipoRischio] = useState("annuale");
  const [giorni, setGiorni] = useState(30);
  const [nAssicurati, setNAssicurati] = useState("");
  const [cov, setCov] = useState({});
  const [slider, setSlider] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/inf/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
  }, []);

  const cat = useMemo(() => config?.categories?.find((c) => c.code === categoria), [config, categoria]);

  useEffect(() => {
    if (!cat) return;
    setModalita(cat.modes[0]?.code || "");
    if (!cat.temporaneo) setTipoRischio("annuale");
    const next = {};
    cat.coverages.forEach((c) => {
      next[c.code] = { selected: c.obbligatoria, valore: c.massimali[Math.floor(c.massimali.length / 2)] || c.massimali[0] };
    });
    setCov(next);
  }, [cat]);

  // Modalità modifica — step 1: campi indipendenti dalla categoria
  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.inf_input || {};
      editRef.current = i;
      setAnagraficaId(q.anagrafica_id || "");
      if (i.categoria) setCategoria(i.categoria);
      if (i.tipo_rischio) setTipoRischio(i.tipo_rischio);
      if (i.giorni != null) setGiorni(i.giorni);
      setNAssicurati(String(i.n_assicurati ?? ""));
      setSlider(i.slider_pct || 0);
      setAnswers(i.underwriting_answers || {});
      if (i.data_effetto) setDataEffetto(i.data_effetto);
      if (q.sinistri) setSinistri(q.sinistri);
      if (q.idd) setIdd(q.idd);
      setEditLoaded(true);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded]);

  // step 2: dopo che l'effetto categoria ha impostato i default, sovrascrive modalità e coperture salvate
  useEffect(() => {
    const i = editRef.current;
    if (!i || !cat || i.categoria !== categoria) return;
    if (i.modalita) setModalita(i.modalita);
    if (i.coverages && Object.keys(i.coverages).length) setCov(i.coverages);
    editRef.current = null;
  }, [cat, categoria]);

  // Pre-compilazione dai dati raccolti via link pubblico (intake) — step 1
  useEffect(() => {
    if (!config || !intakeId || prefilled) return;
    api.get(`/intakes/${intakeId}`).then(({ data }) => {
      const d = data.client_data || {};
      const code = INTAKE_CAT_MAP[d.categoria_assicurati];
      if (code) setCategoria(code);
      if (d.n_persone) setNAssicurati(String(d.n_persone));
      if (d.sinistri) setSinistri(d.sinistri);
      if (d.idd) setIdd(d.idd);
      const garanzie = {};
      Object.keys(d).forEach((k) => { if (k.startsWith("garanzia_")) garanzie[k.slice(9)] = d[k]; });
      intakeRef.current = { code, tipo: d.tipo_rischio, decorrenza: d.decorrenza, scadenza: d.scadenza, garanzie };
      setPrefilled(true);
      toast.success("Dati del cliente pre-compilati dal link");
    }).catch(() => setPrefilled(true));
  }, [config, intakeId, prefilled]);

  // Intake step 2: applica durata/date dopo che l'effetto categoria si è stabilizzato
  useEffect(() => {
    const i = intakeRef.current;
    if (!i || !cat) return;
    if (i.code && cat.code !== i.code) return;
    if (i.garanzie && Object.keys(i.garanzie).length) {
      const covNext = {};
      cat.coverages.forEach((c) => {
        const raw = i.garanzie[c.code];
        const selected = c.obbligatoria || raw != null;
        const valore = raw != null ? Number(raw) : (c.massimali[Math.floor(c.massimali.length / 2)] || c.massimali[0]);
        covNext[c.code] = { selected, valore };
      });
      setCov(covNext);
    }
    if (i.tipo === "Temporanea" && cat.temporaneo) {
      setTipoRischio("temporaneo");
      if (i.decorrenza && i.scadenza) {
        const gg = Math.round((new Date(i.scadenza) - new Date(i.decorrenza)) / 86400000) + 1;
        if (gg > 0) setGiorni(gg);
      }
    }
    if (i.decorrenza) setDataEffetto(i.decorrenza);
    intakeRef.current = null;
  }, [cat]);

  const buildPayload = () => ({
    categoria, modalita, tipo_rischio: tipoRischio, giorni: Number(giorni || 0),
    n_assicurati: Number(nAssicurati || 0), coverages: cov, slider_pct: slider,
    data_effetto: dataEffetto, underwriting_answers: answers, sinistri, idd,
  });

  const calculate = async () => {
    setCalculating(true);
    try {
      const { data } = await api.post("/inf/quotes/calculate", buildPayload());
      setResult(data);
    } catch (e) { toast.error(apiError(e)); } finally { setCalculating(false); }
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/inf/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Rischio non assumibile", msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico richiesto",
        msgs: [...(result.eligibility.warnings || []), ...(result.authority_exceeded ? [`Sconto/aumento oltre la tua autonomia (max +${result.slider_max_autonomo}%).`] : []), "Emissione autonoma bloccata: invia all'Agenzia."] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo standard — emettibile", msgs: [...(result.eligibility.warnings || []), ...minimoMsg(result)] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  const rcQuery = [anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&");
  const premium = result?.premium;
  const labels = config.garanzia_labels;

  return (
    <div className="space-y-6">
      <div>
        <div className={`mb-3 flex flex-wrap gap-2 ${editId ? "hidden" : ""}`} data-testid="product-switch">
          <Link to={`/quota/nuovo${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-rc" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">RC Economia Solidale</Link>
          <Link to={`/quota/mrv${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-mrv" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Tutela Multirischi Volontariato</Link>
          <span className="flex items-center gap-1.5 rounded-full border border-forest bg-forest px-4 py-1.5 text-sm font-medium text-white"><HeartPulse className="h-4 w-4" /> Infortuni Economia Solidale</span>
          <Link to={`/quota/catnat${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-catnat" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">CAT NAT Assimoco</Link>
          <Link to={`/quota/multirischi${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-multirischi" className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">Multirischi Danni ai Beni</Link>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Preventivo — Infortuni per l'Economia Solidale</h1>
        <p className="mt-1 text-muted-foreground">Ed. {config.product_version} · premio minimo annuo {eur(config.min_premio_annuo)} · temporaneo {eur(config.min_premio_temporaneo)}.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Dati di rischio</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Anagrafica cliente</Label>
                <Select value={anagraficaId} onValueChange={setAnagraficaId}>
                  <SelectTrigger data-testid="inf-anagrafica"><SelectValue placeholder="Seleziona ente..." /></SelectTrigger>
                  <SelectContent>{anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Categoria assicurati</Label>
                <Select value={categoria} onValueChange={setCategoria}>
                  <SelectTrigger data-testid="inf-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.categories.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {cat && cat.modes.length > 1 && (
                  <div className="space-y-1.5">
                    <Label>Modalità</Label>
                    <Select value={modalita} onValueChange={setModalita}>
                      <SelectTrigger data-testid="inf-modalita"><SelectValue /></SelectTrigger>
                      <SelectContent>{cat.modes.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Numero assicurati</Label>
                  <Input data-testid="inf-assicurati" type="number" min={1} value={nAssicurati} onChange={(e) => setNAssicurati(e.target.value)} placeholder="es. 15" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Tipo rischio</Label>
                  <Select value={tipoRischio} onValueChange={setTipoRischio}>
                    <SelectTrigger data-testid="inf-tipo-rischio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annuale">Annuale</SelectItem>
                      {cat?.temporaneo && <SelectItem value="temporaneo">Temporaneo (a giornate)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                {tipoRischio === "temporaneo" ? (
                  <div className="space-y-1.5"><Label>Giorni di durata</Label>
                    <Input data-testid="inf-giorni" type="number" min={1} value={giorni} onChange={(e) => setGiorni(e.target.value)} /></div>
                ) : (
                  <div className="space-y-1.5"><Label>Data di effetto</Label>
                    <Input data-testid="inf-data-effetto" type="date" value={dataEffetto} onChange={(e) => setDataEffetto(e.target.value)} /></div>
                )}
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Garanzie e massimali</h2>
            <div className="space-y-3">
              {cat?.coverages.map((c) => (
                <div key={c.code} className="rounded-lg border border-border/60 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-3 text-sm font-medium text-forest">
                      <Checkbox data-testid={`inf-cov-${c.code}`} checked={!!cov[c.code]?.selected} disabled={c.obbligatoria}
                        onCheckedChange={(v) => setCov({ ...cov, [c.code]: { ...cov[c.code], selected: c.obbligatoria ? true : !!v } })} />
                      {labels[c.code]}{c.obbligatoria && <span className="text-[10px] text-muted-foreground">(obbligatoria)</span>}
                    </label>
                    {cov[c.code]?.selected && (
                      <Select value={String(cov[c.code]?.valore)} onValueChange={(v) => setCov({ ...cov, [c.code]: { ...cov[c.code], valore: Number(v) } })}>
                        <SelectTrigger data-testid={`inf-massimale-${c.code}`} className="h-9 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>{c.massimali.map((m) => <SelectItem key={m} value={String(m)}>{c.tipo === "somma" ? eur(m) : `${eur(m)}/gg`}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Questionario assuntivo</h2>
            <div className="space-y-2.5">
              {UW_BOOLS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                  <Checkbox data-testid={`inf-uw-${key}`} checked={!!answers[key]} onCheckedChange={(v) => setAnswers({ ...answers, [key]: !!v })} />
                  <span className="text-forest">{label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 border-t border-border/60 pt-4">
              <SinistriDeclaration value={sinistri} onChange={setSinistri}
                periodoAnni={SINISTRI_YEARS.infortuni_economia_solidale} testidPrefix="inf-sinistri" />
              <div className="mt-4 border-t border-border/60 pt-4">
                <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                <IddDeclaration value={idd} onChange={setIdd} testidPrefix="inf-idd" />
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Leva commerciale</h2>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{slider < 0 ? "Sconto" : "Aumento"}</span>
              <span data-testid="inf-slider-value" className={`font-heading text-2xl font-semibold ${slider < 0 ? "text-terracotta" : "text-forest"}`}>{slider > 0 ? "+" : ""}{slider}%</span>
            </div>
            <Slider data-testid="inf-slider" className="mt-3" value={[slider]} min={sliderMin} max={SLIDER_MAX} step={5} onValueChange={(v) => setSlider(v[0])} />
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>{sliderMin}%</span><span>+300%</span></div>

            <Button data-testid="inf-calculate-button" onClick={calculate} disabled={calculating || !nAssicurati}
              className="mt-5 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {calculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Calculator className="h-5 w-5" /> Calcola premio</>}
            </Button>

            {result && (
              <div className="mt-5 space-y-4" data-testid="inf-quote-result">
                {statusBanner && (
                  <div className={`flex gap-2 rounded-lg border p-3 text-sm ${statusBanner.tone}`}>
                    <statusBanner.icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div><div className="font-medium">{statusBanner.title}</div>
                      {statusBanner.msgs.map((m, i) => <div key={i} className="mt-0.5 text-xs opacity-90">{m}</div>)}</div>
                  </div>
                )}
                {premium && (
                  <div className="rounded-lg bg-sage/50 p-4">
                    {Object.entries(premium.breakdown).map(([k, b]) => (
                      <div key={k} className="flex justify-between py-1 text-sm">
                        <span className="text-muted-foreground">{labels[k] || k}</span>
                        <span className="font-medium text-forest">{eur(b.gross)}</span>
                      </div>
                    ))}
                    <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
                      <span className="font-medium text-forest">Premio annuo lordo</span>
                      <span data-testid="inf-premio-finale" className="font-heading text-2xl font-semibold text-forest">{eur(premium.gross_total)}</span>
                    </div>
                    {premium.tariff_gross_total != null && premium.tariff_gross_total !== premium.gross_total && (
                      <div className="text-right text-xs text-muted-foreground">da tariffa: {eur(premium.tariff_gross_total)}</div>
                    )}
                    {premium.minimum_premium_applied && <div className="mt-1 text-xs text-amber-700">Applicato premio minimo di polizza.</div>}
                  </div>
                )}
                {result.provvigione && (
                  <div data-testid="inf-commission-box" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-terracotta"><Wallet className="h-3.5 w-3.5" /> Provvigione (solo per te)</div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{result.provvigione.pct}% dell'imponibile</span>
                      <span className="font-heading text-xl font-semibold text-terracotta">{eur(result.provvigione.importo)}</span>
                    </div>
                  </div>
                )}
                {result.status !== "non_ammissibile" && (
                  <Button data-testid="inf-save-quote-button" onClick={save} disabled={saving || !anagraficaId}
                    className="h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" /> Salva preventivo</>}
                  </Button>
                )}
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Le provvigioni non compaiono mai nella vista cliente.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
